import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Grid3x3, Clock, Flame, Rocket, ShieldAlert, TrendingUp,
  Play, PauseCircle, RefreshCw, CheckCircle2, AlertTriangle, Zap, UserMinus, DollarSign,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import HelpBubble from "../components/HelpBubble";
import Stat from "../components/optimize/Stat";
import DaypartingPanel from "../components/DaypartingPanel";
import ASCPanel from "../components/ASCPanel";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

const TABS = [
  { key: "matrix", label: "Matrix Launch", icon: Grid3x3 },
  { key: "dayparting", label: "Day-parting", icon: Clock },
  { key: "fatigue", label: "Creative Fatigue", icon: Flame },
  { key: "winners", label: "Winner-Scaling", icon: Rocket },
  { key: "asc", label: "Advantage+ (ASC)", icon: TrendingUp },
  { key: "exclusions", label: "Exclusions", icon: UserMinus },
  { key: "circuit", label: "Circuit Breaker", icon: ShieldAlert },
];

export default function Optimize() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("matrix");

  return (
    <div className="p-6 max-w-[1600px]" data-testid="optimize-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          Optimize <HelpBubble feature="auto_op" />
        </h1>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          Five high-leverage operator tactics · matrix launches, day-parting, fatigue rotation, winner-scaling, circuit breaker
        </p>
      </div>

      <AutoOperatorBanner isAdmin={isAdmin} />

      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-0.5 mb-4 w-fit">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`opt-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded font-mono transition-colors ${
                tab === t.key ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "matrix" && <MatrixPanel />}
      {tab === "dayparting" && <DaypartingPanel />}
      {tab === "fatigue" && <FatiguePanel />}
      {tab === "winners" && <WinnersPanel />}
      {tab === "asc" && <ASCPanel isAdmin={isAdmin} />}
      {tab === "exclusions" && <ExclusionsPanel />}
      {tab === "circuit" && <CircuitPanel isAdmin={isAdmin} />}
    </div>
  );
}

// ---------------- MATRIX LAUNCH ----------------
function MatrixPanel() {
  const [briefs, setBriefs] = useState([]);
  const [briefId, setBriefId] = useState("");
  const [budget, setBudget] = useState(30);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [launch, setLaunch] = useState({ ad_account_id: "", page_id: "", landing_url: "", apply_exclusion: true });
  const [adAccounts, setAdAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeBundle, setActiveBundle] = useState(null);

  const load = async () => {
    try {
      const [{ data: b }, { data: c }, { data: h }, { data: a }] = await Promise.all([
        api.get("/ai/briefs"),
        api.get("/connections/status"),
        api.get("/optimize/matrix/history"),
        api.get("/audiences"),
      ]);
      setBriefs(b || []);
      setAdAccounts(c.fb_ad_accounts || []);
      setHistory(h.matrices || []);
      setActiveBundle((a.bundles || [])[0] || null);
      if (b?.[0]) setBriefId(b[0].brief_id);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const preview = async () => {
    if (!briefId) return toast.error("Pick a brief first");
    setBusy(true);
    try {
      const { data } = await api.post("/optimize/matrix/plan", {
        brief_id: briefId,
        total_budget_usd: parseFloat(budget),
      });
      setPlan(data);
      toast.success(`${data.variant_count} variants · ${fmtMoney(data.per_variant_usd)}/variant`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan failed");
    } finally {
      setBusy(false);
    }
  };

  const go = async () => {
    if (!briefId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/optimize/matrix/launch", {
        brief_id: briefId,
        total_budget_usd: parseFloat(budget),
        ...launch,
      });
      toast.success(
        data.live
          ? `Launched ${data.launched_count}/${data.variant_count} paused on FB`
          : `Saved dry-run · ${data.variant_count} variants`
      );
      const excl = data.exclusion_applied;
      if (excl?.attempted) {
        toast.success(
          excl.live
            ? `Exclusion attached to ${excl.attached_count} adsets (LIVE)`
            : `Exclusion attached to ${excl.attached_count} adsets (simulated)`
        );
      } else if (launch.apply_exclusion && !excl?.attempted) {
        toast.error("Auto-exclusion requested but no bundle exists — build one in Exclusions tab first");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="matrix-panel">
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">Configure matrix <HelpBubble feature="matrix_opt" size="xs" /></div>
        <div>
          <label className="text-[10px] text-slate-500 font-mono">Source brief</label>
          <select
            value={briefId}
            onChange={(e) => setBriefId(e.target.value)}
            data-testid="matrix-brief"
            className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs"
          >
            <option value="">Select a brief…</option>
            {briefs.map((b) => (
              <option key={b.brief_id} value={b.brief_id}>{b.title} · {b.vertical}</option>
            ))}
          </select>
          {briefs.length === 0 && (
            <p className="text-[10px] text-amber-700 mt-1 font-mono">No briefs yet — create one in Campaign Builder first.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-slate-500 font-mono">Total test budget $</label>
          <input
            type="number"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            data-testid="matrix-budget"
            className="w-24 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono"
          />
          <span className="text-[10px] text-slate-500 font-mono">split across 24 (4 angles × 3 audiences × 2 styles)</span>
        </div>
        <button
          onClick={preview}
          disabled={busy}
          data-testid="matrix-plan-btn"
          className="w-full bg-white border border-slate-200 hover:border-blue-400 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
        >
          {busy ? "Planning…" : "Preview plan"}
        </button>

        <div className="pt-3 border-t border-slate-200 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Launch to Facebook (optional)</div>
          <select
            value={launch.ad_account_id}
            onChange={(e) => setLaunch({ ...launch, ad_account_id: e.target.value })}
            data-testid="matrix-ad-account"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
          >
            <option value="">Ad account (blank = simulate)…</option>
            {adAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
          </select>
          <input
            value={launch.page_id}
            onChange={(e) => setLaunch({ ...launch, page_id: e.target.value })}
            placeholder="Page ID"
            data-testid="matrix-page-id"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
          />
          <input
            value={launch.landing_url}
            onChange={(e) => setLaunch({ ...launch, landing_url: e.target.value })}
            placeholder="Landing URL"
            data-testid="matrix-landing"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
          />
          <label className={`flex items-start gap-2 p-2 rounded border text-xs cursor-pointer ${launch.apply_exclusion ? "bg-blue-50 border-blue-200" : "bg-white border-slate-200"}`}>
            <input
              type="checkbox"
              checked={launch.apply_exclusion}
              onChange={(e) => setLaunch({ ...launch, apply_exclusion: e.target.checked })}
              data-testid="matrix-apply-exclusion"
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="text-slate-900 font-medium flex items-center gap-1">
                Auto-apply exclusion audience
                <HelpBubble feature="exclusions" size="xs" />
              </div>
              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                {activeBundle
                  ? `Bundle ${activeBundle.bundle_id} · ${activeBundle.simulated ? "SIM" : "LIVE"} · attaches to all 24 adsets`
                  : "No exclusion bundle yet — build one in Exclusions tab"}
              </div>
            </div>
          </label>
          <button
            onClick={go}
            disabled={busy || !plan}
            data-testid="matrix-launch-btn"
            className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
          >
            {busy ? "Launching…" : (launch.ad_account_id ? "Launch 24 variants (paused on FB)" : "Save dry-run")}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="matrix-preview">
        {!plan && (
          <div className="flex items-center justify-center text-slate-500 text-sm min-h-[300px] border border-dashed border-slate-200 rounded-md">
            Pick a brief and preview the matrix plan.
          </div>
        )}
        {plan && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-900 font-medium">{plan.variant_count} variants</div>
                <div className="text-[10px] text-slate-500 font-mono">
                  {fmtMoney(plan.per_variant_usd)}/variant · total {fmtMoney(plan.matrix_total_budget_usd)}
                </div>
              </div>
            </div>
            <div className="max-h-[500px] overflow-y-auto border border-slate-200 rounded">
              <table className="w-full text-[11px]">
                <thead className="text-[10px] uppercase text-slate-500 bg-white sticky top-0">
                  <tr>
                    <th className="text-left p-2">#</th>
                    <th className="text-left p-2">Angle</th>
                    <th className="text-left p-2">Audience</th>
                    <th className="text-left p-2">Style</th>
                    <th className="text-right p-2">Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.variants.map((v, i) => (
                    <tr key={v.variant_id} data-testid={`matrix-var-${i}`} className="border-t border-slate-200">
                      <td className="p-2 text-slate-500 font-mono">{i + 1}</td>
                      <td className="p-2"><span className="text-[10px] font-mono uppercase text-blue-700 bg-blue-500/10 border border-blue-500/20 rounded px-1.5 py-0.5">{v.angle}</span></td>
                      <td className="p-2 text-slate-600">{v.audience_label}</td>
                      <td className="p-2 text-slate-500 font-mono">{v.style}</td>
                      <td className="p-2 text-right font-mono text-emerald-600">{fmtMoney(v.daily_budget_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {history.length > 0 && (
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-4" data-testid="matrix-history">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Recent matrices</div>
          <div className="space-y-1">
            {history.map((m) => (
              <div key={m.matrix_id} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 text-[11px] font-mono">
                <span className="text-slate-500">{new Date(m.created_at).toLocaleString()}</span>
                <span className="text-slate-900 truncate flex-1">{m.title}</span>
                <span className="text-slate-600">{m.variant_count} variants</span>
                <span className={m.live ? "text-emerald-600" : "text-amber-600"}>{m.live ? "LIVE" : "SIM"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- CREATIVE FATIGUE ----------------
function FatiguePanel() {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/optimize/fatigue");
      setList(data.fatigued || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const rotate = async (id) => {
    setBusy(true);
    try {
      await api.post(`/optimize/fatigue/rotate/${id}`);
      toast.success("Rotated — logged for audit");
      load();
    } catch {
      toast.error("Rotate failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="fatigue-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">
          Ads fatigued ({list.length}) · CTR ≥35% below peak <HelpBubble feature="fatigue" size="xs" />
        </div>
        <button onClick={load} data-testid="fatigue-refresh" className="text-slate-500 hover:text-slate-900">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <div className="text-xs text-slate-500 text-center py-8">No fatigued creatives 🎯</div>}
        {list.map((f) => (
          <div key={f.campaign_id} data-testid={`fatigue-${f.campaign_id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded p-2.5">
            <Flame className={`w-4 h-4 ${f.decay_pct >= 50 ? "text-red-600" : "text-amber-600"}`} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-900 truncate">{f.name}</div>
              <div className="text-[10px] text-slate-500 font-mono">
                {f.vertical} · CTR {f.current_ctr}% (peak {f.peak_ctr}%) · spend {fmtMoney(f.spend)} · ROI {fmtPct(f.roi)}
              </div>
            </div>
            <div className="text-xs font-mono text-red-600">-{f.decay_pct}%</div>
            <button
              onClick={() => rotate(f.campaign_id)}
              disabled={busy}
              data-testid={`fatigue-rotate-${f.campaign_id}`}
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-60"
            >
              Rotate A/B
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- WINNER SCALING ----------------
function WinnersPanel() {
  const [winners, setWinners] = useState([]);
  const [scales, setScales] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: w }, { data: s }] = await Promise.all([
        api.get("/optimize/winners"),
        api.get("/optimize/scales"),
      ]);
      setWinners(w.candidates || []);
      setScales(s.scales || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const scale = async (c) => {
    setBusy(true);
    try {
      const { data } = await api.post("/optimize/winners/scale", { campaign_id: c.campaign_id });
      toast.success(
        data.live
          ? `Scaled ${c.name} · ${data.duplicates?.length || 3} dupes pushed paused`
          : `Scaling plan saved (simulated) · next double in ${data.next_budget_double_in_hours}h`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scale failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="winners-panel">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="flex items-center gap-2 mb-3">
          <Rocket className="w-4 h-4 text-emerald-600" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">
            Winner candidates ({winners.length}) · +50% ROI @ ≥$100/day <HelpBubble feature="winners" size="xs" />
          </div>
        </div>
        <div className="space-y-2">
          {winners.length === 0 && <div className="text-xs text-slate-500 text-center py-8">No winners right now — keep testing.</div>}
          {winners.map((w) => (
            <div key={w.campaign_id} data-testid={`winner-${w.campaign_id}`} className="bg-white border border-emerald-500/30 rounded p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs text-slate-900 font-medium truncate">{w.name}</div>
                <span className="text-[11px] font-mono text-emerald-600">{fmtPct(w.roi)}</span>
              </div>
              <div className="text-[10px] text-slate-500 font-mono mb-2">
                {w.vertical} · spend {fmtMoney(w.spend)} · profit <span className="text-emerald-600">{fmtMoney(w.net_profit)}</span> · budget {fmtMoney(w.daily_budget)}/day
              </div>
              <div className="text-[10px] text-slate-500 font-mono mb-2">
                Plan: duplicate 3× (ages 25-34, 35-44, 45-54) @ {fmtMoney(w.proposed_duplicates[0].budget)}/day each · auto-stop if ROI drops below {w.auto_stop_floor_roi}%
              </div>
              <button
                onClick={() => scale(w)}
                disabled={busy}
                data-testid={`scale-${w.campaign_id}`}
                className="w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-md px-2.5 py-1.5 text-xs font-medium disabled:opacity-60"
              >
                {busy ? "Scaling…" : "Scale this winner"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-3">Scaling log</div>
        <div className="space-y-2">
          {scales.length === 0 && <div className="text-xs text-slate-500 text-center py-8">No scale events yet.</div>}
          {scales.map((s) => (
            <div key={s.scale_id} data-testid={`scale-log-${s.scale_id}`} className="bg-white border border-slate-200 rounded p-2.5 text-[11px]">
              <div className="flex items-center justify-between mb-1">
                <div className="text-slate-900 truncate">{s.source_name}</div>
                <span className={`text-[10px] font-mono ${s.live ? "text-emerald-600" : "text-amber-600"}`}>{s.live ? "LIVE" : "SIM"}</span>
              </div>
              <div className="text-slate-500 font-mono text-[10px]">
                {new Date(s.at).toLocaleString()} · {s.duplicates?.length || 0} dupes · next 2× in {s.next_budget_double_in_hours}h
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- CIRCUIT BREAKER ----------------
function CircuitPanel({ isAdmin }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState({ enabled: false, spend_limit_usd: 500, within_hours: 6, roi_floor_pct: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/optimize/circuit-breaker");
      setData(data);
      if (data.config && Object.keys(data.config).length) {
        setCfg((c) => ({ ...c, ...data.config }));
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/optimize/circuit-breaker", cfg);
      toast.success("Circuit breaker saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const trip = async () => {
    if (!window.confirm("Pause ALL active campaigns right now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/optimize/circuit-breaker/trip");
      toast.success(`Paused ${data.paused_count} campaigns ${data.live ? "(LIVE)" : "(simulated)"}`);
      load();
    } catch {
      toast.error("Trip failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="text-slate-500 text-sm p-4">Loading…</div>;

  const s = data.status;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="circuit-panel">
      <div className={`rounded-md p-5 border ${s.tripped && s.enabled ? "bg-red-500/10 border-red-500/40" : "bg-white border-slate-200"}`}>
        <div className="flex items-center gap-2 mb-3">
          {s.tripped && s.enabled ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
          <div className="text-sm text-slate-900 font-medium">
            {s.tripped && s.enabled ? "BREAKER TRIPPED" : s.enabled ? "Breaker armed · within limits" : "Breaker disabled"}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="circuit-status">
          <Stat label={`Spend (last ${s.within_hours}h)`} value={fmtMoney(s.window_spend)} color={s.window_spend >= s.spend_limit_usd ? "red" : "zinc"} />
          <Stat label="ROI window" value={fmtPct(s.window_roi)} color={s.window_roi >= s.roi_floor_pct ? "emerald" : "red"} />
          <Stat label="Spend limit" value={fmtMoney(s.spend_limit_usd)} color="zinc" />
          <Stat label="ROI floor" value={fmtPct(s.roi_floor_pct)} color="zinc" />
        </div>
        {data.last_event && (
          <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] font-mono text-slate-500" data-testid="circuit-last">
            Last trip: {new Date(data.last_event.at).toLocaleString()} · paused {data.last_event.paused_count} campaigns
          </div>
        )}
        <button
          onClick={trip}
          disabled={busy}
          data-testid="circuit-trip-btn"
          className="mt-4 w-full bg-red-600 text-white hover:bg-red-700 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
        >
          <PauseCircle className="w-4 h-4 inline mr-1" /> Pause all campaigns now
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">
          Auto-trip config {!isAdmin && <span className="text-amber-700">(admin only)</span>} <HelpBubble feature="circuit" size="xs" />
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            data-testid="circuit-enabled"
            disabled={!isAdmin}
          />
          Enable auto-trip (evaluated every 30 min)
        </label>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-slate-500 font-mono">Spend limit $</label>
            <input
              type="number"
              value={cfg.spend_limit_usd}
              onChange={(e) => setCfg({ ...cfg, spend_limit_usd: parseFloat(e.target.value) })}
              data-testid="circuit-spend"
              disabled={!isAdmin}
              className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-60"
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-mono">Within hours</label>
            <input
              type="number"
              value={cfg.within_hours}
              onChange={(e) => setCfg({ ...cfg, within_hours: parseInt(e.target.value) })}
              data-testid="circuit-hours"
              disabled={!isAdmin}
              className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-60"
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-mono">ROI floor %</label>
            <input
              type="number"
              value={cfg.roi_floor_pct}
              onChange={(e) => setCfg({ ...cfg, roi_floor_pct: parseFloat(e.target.value) })}
              data-testid="circuit-roi"
              disabled={!isAdmin}
              className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-60"
            />
          </div>
        </div>
        <div className="text-[10px] text-slate-500 font-mono leading-relaxed">
          If workspace spend ≥ <b>{fmtMoney(cfg.spend_limit_usd)}</b> in <b>{cfg.within_hours}h</b> and ROI &lt; <b>{cfg.roi_floor_pct}%</b>, pause every ACTIVE campaign.
        </div>
        <button
          onClick={save}
          disabled={busy || !isAdmin}
          data-testid="circuit-save-btn"
          className="w-full bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save config"}
        </button>
      </div>
    </div>
  );
}

// ---------------- EXCLUSION AUDIENCES ----------------
function ExclusionsPanel() {
  const [savings, setSavings] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [form, setForm] = useState({ ad_account_id: "", page_id: "", retention_days: 30 });
  const [adAccounts, setAdAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adsetIds, setAdsetIds] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: b }, { data: c }] = await Promise.all([
        api.get("/audiences/savings"),
        api.get("/audiences"),
        api.get("/connections/status"),
      ]);
      setSavings(s);
      setBundles(b.bundles || []);
      setAdAccounts(c.fb_ad_accounts || []);
      if (c.fb_ad_accounts?.[0] && !form.ad_account_id) {
        setForm((f) => ({ ...f, ad_account_id: c.fb_ad_accounts[0].id }));
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const build = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/audiences/build-exclusion", form);
      if (!data.ok) return toast.error(data.error || "Build failed");
      toast.success(
        data.simulated
          ? `Bundle built (simulated) · ${data.converter_count} converters`
          : `Bundle built LIVE · audience ${data.exclusion_audience_id}`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!selected) return toast.error("Select a bundle first");
    const ids = adsetIds.split(/[,\s]+/).filter(Boolean);
    if (!ids.length) return toast.error("Paste at least one adset ID");
    setBusy(true);
    try {
      const { data } = await api.post("/audiences/apply", {
        bundle_id: selected.bundle_id, adset_ids: ids,
      });
      if (!data.ok) return toast.error(data.error || "Apply failed");
      const simCount = data.results.filter((r) => r.simulated).length;
      toast.success(
        simCount === data.results.length
          ? `Attached (simulated) to ${data.applied} adsets`
          : `Attached LIVE to ${data.applied - simCount} adsets`
      );
      setAdsetIds("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="exclusions-panel">
      {savings && (
        <div className="grid grid-cols-4 gap-3" data-testid="exclusions-savings">
          <Stat label="Clicks (7d)" value={Number(savings.total_clicks).toLocaleString()} />
          <Stat label="Avg CPC" value={`$${savings.avg_cpc}`} />
          <Stat label="Converters" value={savings.converter_count} color={savings.converter_count > 0 ? "emerald" : "amber"} sub="from CAPI log" />
          <Stat
            label="Est monthly waste"
            value={`$${Number(savings.est_monthly_savings_usd).toLocaleString()}`}
            color="red"
            sub={`${savings.est_waste_pct_without_exclusion}% of re-click spend`}
          />
        </div>
      )}

      <div className="bg-gradient-to-r from-red-50 to-amber-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
        <DollarSign className="w-5 h-5 text-red-600 mt-0.5" />
        <div className="text-sm text-slate-900 leading-relaxed">
          <b>Why this matters:</b> Meta keeps re-showing ads to people who clicked once and bounced. You pay per click, every click. Build an exclusion audience (clickers last 30d <i>minus</i> converters from your CAPI log), attach it to each adset, and expect a 15–30% drop in CPA within a week.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">1. Build exclusion bundle <HelpBubble feature="exclusions" size="xs" /></div>
          <select
            value={form.ad_account_id}
            onChange={(e) => setForm({ ...form, ad_account_id: e.target.value })}
            data-testid="excl-ad-account"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
          >
            <option value="">Ad account (blank = simulate)…</option>
            {adAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.id})</option>)}
          </select>
          <input
            value={form.page_id}
            onChange={(e) => setForm({ ...form, page_id: e.target.value })}
            placeholder="FB Page ID (required for live)"
            data-testid="excl-page-id"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
          />
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-slate-500 font-mono">Retention days</label>
            <input
              type="number"
              value={form.retention_days}
              onChange={(e) => setForm({ ...form, retention_days: parseInt(e.target.value) })}
              data-testid="excl-retention"
              className="w-20 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono"
            />
          </div>
          <button
            onClick={build}
            disabled={busy}
            data-testid="excl-build-btn"
            className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
          >
            {busy ? "Building…" : "Build clickers-minus-converters bundle"}
          </button>
          <p className="text-[10px] text-slate-500 font-mono leading-relaxed">
            Creates a Page Engagement audience (all ad clickers) + a Customer List from your CAPI Purchase log, then a SUBTRACT audience. Runs in simulation when FB creds are missing.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">2. Apply to adsets</div>
          {!selected && <div className="text-xs text-slate-500">Click a bundle below to select it, then paste adset IDs here.</div>}
          {selected && (
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[11px] font-mono" data-testid="excl-selected">
              <div>Selected bundle: <b>{selected.bundle_id}</b></div>
              <div className="text-slate-500">Exclusion audience: {selected.exclusion_audience_id}</div>
            </div>
          )}
          <textarea
            value={adsetIds}
            onChange={(e) => setAdsetIds(e.target.value)}
            rows={4}
            placeholder="Paste one or more adset IDs, separated by space or comma"
            data-testid="excl-adset-ids"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
          />
          <button
            onClick={apply}
            disabled={busy || !selected}
            data-testid="excl-apply-btn"
            className="w-full bg-red-600 text-white hover:bg-red-700 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
          >
            {busy ? "Applying…" : "Attach exclusion to adsets"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="excl-bundles">
        <div className="px-4 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-mono">
          Your exclusion bundles
        </div>
        {bundles.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No bundles yet — build your first one above.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
              <tr>
                <th className="text-left p-2.5">Created</th>
                <th className="text-left p-2.5">Bundle</th>
                <th className="text-left p-2.5">Exclusion audience</th>
                <th className="text-right p-2.5">Converters</th>
                <th className="text-right p-2.5">Retention</th>
                <th className="text-right p-2.5">Mode</th>
              </tr>
            </thead>
            <tbody>
              {bundles.map((b, i) => (
                <tr
                  key={b.bundle_id}
                  data-testid={`excl-row-${i}`}
                  onClick={() => setSelected(b)}
                  className={`border-t border-slate-200 cursor-pointer hover:bg-slate-50 ${selected?.bundle_id === b.bundle_id ? "bg-blue-50" : ""}`}
                >
                  <td className="p-2.5 font-mono text-[10px] text-slate-500">{new Date(b.created_at).toLocaleString()}</td>
                  <td className="p-2.5 font-mono text-slate-900">{b.bundle_id}</td>
                  <td className="p-2.5 font-mono text-slate-500">{b.exclusion_audience_id}</td>
                  <td className="p-2.5 text-right font-mono text-slate-900">{b.converter_count}</td>
                  <td className="p-2.5 text-right font-mono text-slate-500">{b.retention_days}d</td>
                  <td className="p-2.5 text-right">
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${b.simulated ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {b.simulated ? "SIM" : "LIVE"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ---------------- AUTO-OPERATOR ----------------
function AutoOperatorBanner({ isAdmin }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState({
    enabled: false, auto_fatigue: true, auto_winners: true,
    fatigue_threshold_pct: 50, winner_min_roi_pct: 50, debounce_hours: 24,
  });
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/optimize/auto-operator");
      setData(data);
      if (data.config && Object.keys(data.config).length) {
        setCfg((c) => ({ ...c, ...data.config }));
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/optimize/auto-operator", cfg);
      toast.success(cfg.enabled ? "Auto-Operator armed — runs every 30 min" : "Auto-Operator disabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/optimize/auto-operator/run-now");
      toast.success(
        `Ran · rotated ${data.fatigue?.rotated?.length || 0} fatigued · scaled ${data.winners?.scaled?.length || 0} winners`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const isOn = !!cfg.enabled;

  return (
    <div
      data-testid="auto-operator-banner"
      className={`mb-4 rounded-md border ${isOn ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" : "bg-white border-slate-200"}`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${isOn ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          <Zap className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            Auto-Operator
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${isOn ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>
              {isOn ? "ARMED" : "OFF"}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 font-mono">
            {isOn
              ? `Auto-rotates fatigued creatives (>${cfg.fatigue_threshold_pct}% decay) and scales winners (>${cfg.winner_min_roi_pct}% ROI) every 30 min · debounced ${cfg.debounce_hours}h`
              : "One-switch overnight automation · rotates fatigued creatives + scales winners on a schedule"}
          </div>
          {data.last_run && (
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">
              Last run {new Date(data.last_run.at).toLocaleString()} · rotated {data.last_run.fatigue?.rotated?.length || 0}, scaled {data.last_run.winners?.scaled?.length || 0}
            </div>
          )}
        </div>
        {isOn && (
          <button
            onClick={runNow}
            disabled={busy}
            data-testid="ao-run-now-btn"
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60"
          >
            {busy ? "Running…" : "Run now"}
          </button>
        )}
        <button
          onClick={() => setExpanded((x) => !x)}
          data-testid="ao-expand-btn"
          className="text-xs text-slate-600 hover:text-slate-900 font-mono px-3 py-1.5 border border-slate-200 rounded-md hover:bg-white"
        >
          {expanded ? "Hide" : "Configure"}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-200 px-4 py-4 bg-white/70 rounded-b-md">
          {!isAdmin && <div className="text-[11px] text-amber-700 font-mono mb-2">Admin only — your changes won't save.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-900">
              <input
                type="checkbox"
                checked={!!cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                data-testid="ao-enabled"
                disabled={!isAdmin}
                className="rounded"
              />
              Enable Auto-Operator (evaluates every 30 min)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-900">
              <input
                type="checkbox"
                checked={!!cfg.auto_fatigue}
                onChange={(e) => setCfg({ ...cfg, auto_fatigue: e.target.checked })}
                data-testid="ao-fatigue"
                disabled={!isAdmin}
              />
              Auto-rotate fatigued creatives
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-900">
              <input
                type="checkbox"
                checked={!!cfg.auto_winners}
                onChange={(e) => setCfg({ ...cfg, auto_winners: e.target.checked })}
                data-testid="ao-winners"
                disabled={!isAdmin}
              />
              Auto-scale winners (duplicate 3× at 2× budget)
            </label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 font-mono">Fatigue decay ≥%</label>
                <input
                  type="number"
                  value={cfg.fatigue_threshold_pct}
                  onChange={(e) => setCfg({ ...cfg, fatigue_threshold_pct: parseFloat(e.target.value) })}
                  disabled={!isAdmin}
                  data-testid="ao-fatigue-pct"
                  className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-60"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-mono">Winner ROI ≥%</label>
                <input
                  type="number"
                  value={cfg.winner_min_roi_pct}
                  onChange={(e) => setCfg({ ...cfg, winner_min_roi_pct: parseFloat(e.target.value) })}
                  disabled={!isAdmin}
                  data-testid="ao-winner-pct"
                  className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-60"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 font-mono">Debounce (hrs)</label>
                <input
                  type="number"
                  value={cfg.debounce_hours}
                  onChange={(e) => setCfg({ ...cfg, debounce_hours: parseInt(e.target.value) })}
                  disabled={!isAdmin}
                  data-testid="ao-debounce"
                  className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-60"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
            <div className="text-[10px] text-slate-500 font-mono">
              Recent runs: {data.history?.length || 0}
            </div>
            <button
              onClick={save}
              disabled={busy || !isAdmin}
              data-testid="ao-save-btn"
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-md px-4 py-1.5 text-xs font-medium disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save Auto-Operator"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



